import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  BatchSaveTalentReviewScoresResult,
  BatchUpdateTalentReviewJobProfileStatusPayload,
  BatchUpdateTalentReviewJobProfileStatusResult,
  CreateTalentReviewCyclePayload,
  DuplicateTalentReviewJobProfilePayload,
  LockTalentReviewCyclePayload,
  TalentReviewCarryForwardResult,
  TalentReviewChangeSummary,
  QueryTalentReviewCycleParams,
  QueryTalentReviewDashboardParams,
  QueryTalentReviewAnalyticsParams,
  QueryTalentReviewJobProfileParams,
  QueryTalentReviewParticipantParams,
  QueryTalentReviewResultParams,
  TalentReviewCalculationResult,
  TalentReviewContinuityPreview,
  TalentReviewCycle,
  TalentReviewDashboard,
  TalentReviewDashboardAnalytics,
  TalentReviewEmployeeDetail,
  TalentReviewEmployeeFit,
  TalentReviewEmployeeProfileDetail,
  TalentReviewEmployeeReport,
  TalentReviewImportResult,
  TalentReviewImportRow,
  TalentReviewJobProfile,
  TalentReviewJobProfilePayload,
  TalentReviewPageResult,
  TalentReviewParticipant,
  TalentReviewResult,
  TalentReviewScore,
  TalentReviewScoreItem,
  TalentReviewTeamReport,
  UpdateTalentReviewDevelopmentActionPayload,
  UpdateTalentReviewJobProfileStatusPayload,
  UpdateTalentReviewParticipantPayload,
} from './types/talent-review';

const request = createAppRequest();
const TALENT_REVIEW_PREFIX = '/bff/talent-review';

export async function getTalentReviewCycles(
  params?: QueryTalentReviewCycleParams,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewPageResult<TalentReviewCycle>>(
    `${TALENT_REVIEW_PREFIX}/cycles`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function createTalentReviewCycle(
  body: CreateTalentReviewCyclePayload,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewCycle>(`${TALENT_REVIEW_PREFIX}/cycles`, {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

export async function getTalentReviewCycle(
  cycleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewCycle>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function removeTalentReviewCycle(
  cycleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}

export async function getTalentReviewParticipants(
  cycleUuid: string,
  params?: QueryTalentReviewParticipantParams,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewPageResult<TalentReviewParticipant>>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/participants`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function getTalentReviewContinuityPreview(
  cycleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewContinuityPreview>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/continuity-preview`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function carryForwardTalentReviewCycle(
  cycleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewCarryForwardResult>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/carry-forward`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
}

export async function importTalentReviewParticipants(
  cycleUuid: string,
  rows: TalentReviewImportRow[],
  options?: ApiRequestOptions,
) {
  return request<TalentReviewImportResult>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/participants/import`,
    {
      method: 'POST',
      data: { rows },
      ...(options || {}),
    },
  );
}

export async function importTalentReviewParticipantsFile(
  cycleUuid: string,
  file: File,
  options?: ApiRequestOptions,
) {
  const formData = new FormData();
  formData.append('file', file);

  return request<TalentReviewImportResult>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/participants/import-file`,
    {
      method: 'POST',
      data: formData,
      ...(options || {}),
    },
  );
}

export async function getTalentReviewImportTemplate(
  options?: ApiRequestOptions,
) {
  return request<Blob>(`${TALENT_REVIEW_PREFIX}/participants/import-template`, {
    method: 'GET',
    responseType: 'blob',
    ...(options || {}),
  });
}

export async function updateTalentReviewParticipant(
  participantUuid: string,
  body: UpdateTalentReviewParticipantPayload,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewParticipant>(
    `${TALENT_REVIEW_PREFIX}/participants/${participantUuid}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
}

export async function confirmTalentReviewParticipants(
  cycleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean }>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/participants/confirm`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
}

export async function batchSaveTalentReviewScores(
  cycleUuid: string,
  items: TalentReviewScoreItem[],
  submit?: boolean,
  options?: ApiRequestOptions,
) {
  return request<BatchSaveTalentReviewScoresResult>(
    `${TALENT_REVIEW_PREFIX}/scores/batch`,
    {
      method: 'POST',
      data: {
        cycleUuid,
        items,
        submit,
      },
      ...(options || {}),
    },
  );
}

export async function getTalentReviewScores(
  cycleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewScore[]>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/scores`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function calculateTalentReviewResults(
  cycleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewCalculationResult>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/calculate`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
}

export async function getTalentReviewResults(
  cycleUuid: string,
  params?: QueryTalentReviewResultParams,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewPageResult<TalentReviewResult>>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/results`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function getTalentReviewChangeSummary(
  cycleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewChangeSummary>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/change-summary`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function lockTalentReviewCycle(
  cycleUuid: string,
  body: LockTalentReviewCyclePayload,
  options?: ApiRequestOptions,
) {
  return request<Pick<TalentReviewCycle, 'uuid' | 'status' | 'lockedAt'>>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/lock`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
}

export async function getTalentReviewEmployeeDetail(
  employeeUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewEmployeeDetail>(
    `${TALENT_REVIEW_PREFIX}/employees/${employeeUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function getTalentReviewEmployeeProfileDetail(
  employeeNo: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewEmployeeProfileDetail>(
    `${TALENT_REVIEW_PREFIX}/employees/profile/${encodeURIComponent(employeeNo)}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function getTalentReviewDashboard(
  params?: QueryTalentReviewDashboardParams,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewDashboard>(`${TALENT_REVIEW_PREFIX}/dashboard`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getTalentReviewJobProfiles(
  params?: QueryTalentReviewJobProfileParams,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewPageResult<TalentReviewJobProfile>>(
    `${TALENT_REVIEW_PREFIX}/job-profiles`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function createTalentReviewJobProfile(
  body: TalentReviewJobProfilePayload,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewJobProfile>(`${TALENT_REVIEW_PREFIX}/job-profiles`, {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

export async function getTalentReviewJobProfile(
  jobProfileUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewJobProfile>(
    `${TALENT_REVIEW_PREFIX}/job-profiles/${jobProfileUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function duplicateTalentReviewJobProfile(
  jobProfileUuid: string,
  body?: DuplicateTalentReviewJobProfilePayload,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewJobProfile>(
    `${TALENT_REVIEW_PREFIX}/job-profiles/${jobProfileUuid}/duplicate`,
    {
      method: 'POST',
      data: body || {},
      ...(options || {}),
    },
  );
}

export async function batchUpdateTalentReviewJobProfileStatus(
  body: BatchUpdateTalentReviewJobProfileStatusPayload,
  options?: ApiRequestOptions,
) {
  return request<BatchUpdateTalentReviewJobProfileStatusResult>(
    `${TALENT_REVIEW_PREFIX}/job-profiles/status`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
}

export async function updateTalentReviewJobProfileStatus(
  jobProfileUuid: string,
  body: UpdateTalentReviewJobProfileStatusPayload,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewJobProfile>(
    `${TALENT_REVIEW_PREFIX}/job-profiles/${jobProfileUuid}/status`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
}

export async function updateTalentReviewJobProfile(
  jobProfileUuid: string,
  body: TalentReviewJobProfilePayload,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewJobProfile>(
    `${TALENT_REVIEW_PREFIX}/job-profiles/${jobProfileUuid}`,
    {
      method: 'PUT',
      data: body,
      ...(options || {}),
    },
  );
}

export async function getTalentReviewEmployeeFit(
  employeeNo: string,
  params?: { jobProfileUuid?: string },
  options?: ApiRequestOptions,
) {
  return request<TalentReviewEmployeeFit>(
    `${TALENT_REVIEW_PREFIX}/employees/profile/${encodeURIComponent(employeeNo)}/fit`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function getTalentReviewEmployeeReport(
  employeeNo: string,
  params?: { jobProfileUuid?: string },
  options?: ApiRequestOptions,
) {
  return request<TalentReviewEmployeeReport>(
    `${TALENT_REVIEW_PREFIX}/employees/profile/${encodeURIComponent(employeeNo)}/report`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function updateTalentReviewDevelopmentAction(
  employeeNo: string,
  actionType: string,
  body: UpdateTalentReviewDevelopmentActionPayload,
  options?: ApiRequestOptions,
) {
  return request(
    `${TALENT_REVIEW_PREFIX}/employees/profile/${encodeURIComponent(
      employeeNo,
    )}/development-actions/${encodeURIComponent(actionType)}`,
    {
      method: 'PATCH',
      data: body,
      ...(options || {}),
    },
  );
}

export async function getTalentReviewTeamReport(
  cycleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewTeamReport>(
    `${TALENT_REVIEW_PREFIX}/cycles/${cycleUuid}/team-report`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function getTalentReviewDashboardAnalytics(
  params?: QueryTalentReviewAnalyticsParams,
  options?: ApiRequestOptions,
) {
  return request<TalentReviewDashboardAnalytics>(
    `${TALENT_REVIEW_PREFIX}/dashboard/analytics`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}
